// client/src/components/marketer/MarketerBizHome.js
// 비즈 홈 — 마이비즈 첫 화면 (시안 redesign-v3-mybiz-bold.html A안)
// 기존 목록 API 3종을 조합해 "지금 처리할 일"과 진행 프로젝트 현황을 보여준다.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePoints } from '../../contexts/PointContext';
import '../../styles/marketer/mb-home.css';

const WEEKDAYS = ['일', '월', '화', '수', '목', '금', '토'];
const STAGE_COUNT = 6; // 모집 → 선정 → 진행 → 검수 → 정산 → 완료

const SERVICE_LABEL = { delivery: '배송형', purchase: '구매형', visit: '방문형', free: '자율형' };

function dday(date) {
  if (!date) return null;
  const diff = new Date(date).getTime() - Date.now();
  return Math.ceil(diff / (24 * 60 * 60 * 1000));
}

function shortDate(date) {
  if (!date) return '';
  const d = new Date(date);
  return `${d.getMonth() + 1}/${d.getDate()}`;
}

// 프로젝트의 현재 단계 판정 (1 모집 ~ 6 완료)
function stageOf(project, applyStat) {
  const now = Date.now();
  const recruitEnd = project.schedule?.recruitEnd ? new Date(project.schedule.recruitEnd).getTime() : null;
  const submitEnd = project.schedule?.submitEnd ? new Date(project.schedule.submitEnd).getTime() : null;
  const submitted = (project.contractedInfluencers || []).filter((a) => a.resultStatus === 'submitted').length;

  if (submitted > 0) return { n: 4, label: '결과물 검수' };
  if (recruitEnd && now < recruitEnd) {
    const applied = applyStat?.appliedCount || 0;
    return applied > 0 ? { n: 2, label: '인플루언서 선정' } : { n: 1, label: '모집 중' };
  }
  if (submitEnd && now < submitEnd) return { n: 3, label: '콘텐츠 진행' };
  if (project.perPurchaseReward?.use) return { n: 5, label: 'CPS 정산' };
  return { n: 5, label: '정산·완료 대기' };
}

function Pipeline({ stage }) {
  return (
    <span className="mbh-pipe" aria-hidden="true">
      {Array.from({ length: STAGE_COUNT }, (_, i) => {
        const cls = i + 1 < stage ? 'is-done' : i + 1 === stage ? 'is-now' : '';
        return <i key={i} className={cls} />;
      })}
    </span>
  );
}

function MarketerBizHome() {
  const nav = useNavigate();
  const { points, loading: pointsLoading } = usePoints();

  const [projects, setProjects] = useState(null); // null = 로딩 중
  const [applyStats, setApplyStats] = useState([]);
  const [cpsDue, setCpsDue] = useState([]);
  const [notices, setNotices] = useState([]);
  const [error, setError] = useState(false);

  // 새 소식 (구 /marketer/home의 공지 영역 흡수)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch('/api/notices?page=1&pageSize=3', { credentials: 'include' });
        const data = await res.json().catch(() => ({}));
        if (!cancelled && data.ok) setNotices(data.items || []);
      } catch {}
    })();
    return () => { cancelled = true; };
  }, []);

  const go = useCallback((tab, sub) => nav(`/marketer/mybiz?tab=${tab}&sub=${sub}`), [nav]);

  const load = useCallback(async () => {
    setError(false);
    setProjects(null);
    try {
      const [ongoingRes, applyRes, cpsRes] = await Promise.all([
        fetch('/api/marketer/projects/ongoing', { credentials: 'include' }),
        fetch('/api/marketer/projects/with-applications', { credentials: 'include' }),
        fetch('/api/marketer/cps-settlements?filter=in_progress', { credentials: 'include' }),
      ]);
      const ongoing = await ongoingRes.json().catch(() => ({}));
      const apply = await applyRes.json().catch(() => ({}));
      const cps = await cpsRes.json().catch(() => ({}));

      if (!ongoingRes.ok || !ongoing.ok) throw new Error('ongoing');

      setProjects(ongoing.projects || []);
      setApplyStats(apply.ok ? apply.projects || [] : []);
      setCpsDue(cps.ok ? (cps.projects || []).filter((p) => p.isInProgress) : []);
    } catch {
      setProjects([]);
      setError(true);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const applyStatById = useMemo(() => {
    const map = new Map();
    applyStats.forEach((p) => map.set(String(p.id), p));
    return map;
  }, [applyStats]);

  // ── 할 일 집계 ──
  const todos = useMemo(() => {
    if (!projects) return [];
    const now = Date.now();
    const items = [];

    // 1) 결과물 검수 대기
    let reviewCount = 0;
    let reviewProject = null;
    projects.forEach((p) => {
      const n = (p.contractedInfluencers || []).filter((a) => a.resultStatus === 'submitted').length;
      if (n > 0 && !reviewProject) reviewProject = p;
      reviewCount += n;
    });
    if (reviewCount > 0) {
      items.push({
        key: 'review', ico: '📝', mood: 'amber',
        title: <>결과물 <b>{reviewCount}건</b>이 승인을 기다려요</>,
        sub: `${reviewProject?.productName || ''} 프로젝트 · 7일 경과 시 자동 승인돼요`,
        action: '검수하기', onClick: () => go('project', 'prjResult'),
      });
    }

    // 2) CPS 결산 도래
    if (cpsDue.length > 0) {
      const first = cpsDue[0];
      items.push({
        key: 'cps', ico: '💰', mood: 'blue',
        title: <>CPS 결산일이 됐어요{cpsDue.length > 1 ? <> · <b>{cpsDue.length}건</b></> : null}</>,
        sub: `${first.productName || first.product?.name || first.projectNumber || ''} · 유입 고객 수만 입력하면 끝나요`,
        action: '정산하기', onClick: () => go('project', 'prjCpsCharge'),
      });
    }

    // 3) 새 지원자 선정 대기 (모집 마감 전)
    let applicantCount = 0;
    let applicantDday = null;
    let applicantProject = null;
    applyStats.forEach((p) => {
      const end = p.recruitEnd || p.schedule?.recruitEnd;
      if (!end || new Date(end).getTime() <= now) return;
      const waiting = Math.max(0, (p.appliedCount || 0) - (p.selectedCount || 0));
      if (waiting > 0) {
        applicantCount += waiting;
        const d = dday(end);
        if (applicantDday === null || (d !== null && d < applicantDday)) {
          applicantDday = d;
          applicantProject = p;
        }
      }
    });
    if (applicantCount > 0) {
      items.push({
        key: 'select', ico: '🙋', mood: 'violet',
        title: <>새 지원자 <b>{applicantCount}명</b>이 선정을 기다려요</>,
        sub: `${applicantProject?.productName || applicantProject?.projectNumber || ''} · 모집 마감 D-${applicantDday}`,
        action: '선정하기', onClick: () => go('project', 'prjSelect'),
      });
    }

    // 4) 계약 응답 대기
    const pendingContract = applyStats.reduce((sum, p) => sum + (p.pendingContractCount || 0), 0);
    if (pendingContract > 0) {
      items.push({
        key: 'contract', ico: '🤝', mood: 'blue',
        title: <>계약 응답 대기 <b>{pendingContract}명</b></>,
        sub: '인플루언서의 계약 수락을 기다리고 있어요',
        action: '보기', onClick: () => go('project', 'prjSelect'),
      });
    }

    return items;
  }, [projects, applyStats, cpsDue, go]);

  // ── 프로젝트 카드 데이터 ──
  const cards = useMemo(() => {
    if (!projects) return [];
    return projects.map((p) => {
      const stat = applyStatById.get(String(p.id));
      const stage = stageOf(p, stat);
      const submitted = (p.contractedInfluencers || []).filter((a) => a.resultStatus === 'submitted').length;
      const contracted = (p.contractedInfluencers || []).length;
      const headcount = p.recruit?.headcount || p.headcount || 0;

      // 우측 핵심 숫자: 단계별로 지금 중요한 값 하나
      let num = '';
      let unit = '';
      let numClass = '';
      if (stage.n === 4) { num = `${submitted}건`; unit = '승인 대기'; numClass = 'is-amber'; }
      else if (stage.n === 5 && cpsDue.some((c) => String(c.id || c._id) === String(p.id))) { num = '오늘'; unit = '결산일'; numClass = 'is-blue'; }
      else if (stage.n <= 2) { num = `${stat?.appliedCount || 0}명`; unit = '지원'; }
      else if (stage.n === 3) { num = shortDate(p.schedule?.submitEnd); unit = '제출 마감'; }
      else { num = `${contracted}/${headcount}`; unit = '계약'; }

      const subParts = [
        `NO.${p.projectNumber}`,
        SERVICE_LABEL[p.serviceType] || p.serviceType,
        `계약 ${contracted}/${headcount}명`,
      ];

      // 클릭 시 해당 단계 화면으로
      const target =
        stage.n === 4 ? 'prjResult'
        : stage.n === 5 && p.perPurchaseReward?.use ? 'prjCpsCharge'
        : stage.n === 2 ? 'prjSelect'
        : 'prjOn';

      return {
        id: p.id, name: p.productName, cover: p.cover,
        sub: subParts.join(' · '), stage, num, unit, numClass,
        onClick: () => go('project', target),
      };
    });
  }, [projects, applyStatById, cpsDue, go]);

  const today = new Date();
  const dateLabel = `${today.getMonth() + 1}월 ${today.getDate()}일 ${WEEKDAYS[today.getDay()]}요일`;
  const loading = projects === null;

  return (
    <div className="mbh">
      <div className="mbh-grid">
        {/* 운영 브리핑 */}
        <section className="mbh-brief" aria-label="오늘의 할 일">
          <div className="mbh-date">{dateLabel}</div>
          <h2 className="mbh-title">
            {loading ? '할 일을 확인하고 있어요' : todos.length > 0
              ? <>지금 처리할 일이 <b>{todos.length}건</b> 있어요</>
              : '지금 처리할 일이 없어요'}
          </h2>

          {loading && <div className="mbh-loading">불러오는 중…</div>}

          {!loading && error && (
            <div className="mbh-error">
              데이터를 불러오지 못했어요.
              <button type="button" onClick={load}>다시 시도</button>
            </div>
          )}

          {!loading && !error && todos.length === 0 && (
            <div className="mbh-todo__empty">모든 할 일을 처리했어요. 새 프로젝트를 시작해보세요! ✨</div>
          )}

          {!loading && !error && todos.length > 0 && (
            <div className="mbh-todo">
              {todos.map((t) => (
                <button key={t.key} type="button" className="mbh-todo__item" onClick={t.onClick}>
                  <span className={`mbh-todo__ico mbh-todo__ico--${t.mood}`} aria-hidden="true">{t.ico}</span>
                  <span className="mbh-todo__meta">
                    <span className="mbh-todo__t">{t.title}</span>
                    <span className="mbh-todo__s">{t.sub}</span>
                  </span>
                  <span className="mbh-todo__go">{t.action} →</span>
                </button>
              ))}
            </div>
          )}
        </section>

        {/* 포인트 월렛 */}
        <div>
          <section className="mbh-wallet" aria-label="보유 포인트">
            <div className="mbh-wallet__label">보유 포인트</div>
            <div className="mbh-wallet__amt">
              {pointsLoading ? '…' : Number(points || 0).toLocaleString()}<small>P</small>
            </div>
            <div className="mbh-wallet__sub">
              {loading ? ' ' : `진행 중인 프로젝트 ${projects.length}개`}
            </div>
            <div className="mbh-wallet__cta">
              <button type="button" className="mbh-btn-white" onClick={() => go('point', 'pointCharge')}>충전하기</button>
              <button type="button" className="mbh-btn-glass" onClick={() => go('point', 'pointList')}>이용내역</button>
            </div>
          </section>

          {/* 새 소식: 공지 0건이어도 카드·전체 보기 진입점은 항상 노출 */}
          <section className="mbh-spend" aria-label="새 소식" style={{ marginBottom: 16 }}>
              <div className="mbh-spend__row" style={{ paddingTop: 2 }}>
                <span style={{ fontWeight: 800, color: 'var(--mb-text-title)', fontSize: 14 }}>새 소식</span>
                <b>
                  <button
                    type="button"
                    onClick={() => {
                      try { sessionStorage.setItem('current_user_role', 'marketer'); } catch {}
                      nav('/notice', { state: { role: 'marketer' } });
                    }}
                    style={{ border: 0, background: 'none', color: '#1f4fd6', fontWeight: 800, cursor: 'pointer', fontFamily: 'inherit', fontSize: 13, padding: 0 }}
                  >
                    전체 보기 →
                  </button>
                </b>
              </div>
              {notices.length === 0 && (
                <div className="mbh-spend__row"><span>새로운 공지가 없어요</span></div>
              )}
              {notices.map((n) => (
                <div className="mbh-spend__row" key={n._id} style={{ gap: 12 }}>
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, color: 'var(--mb-text-title)', fontWeight: 600 }}>
                    {n.important ? '📌 ' : ''}{n.title}
                  </span>
                  <b>
                    <button
                      type="button"
                      onClick={() => nav(`/notice/${n._id}`)}
                      style={{ border: 0, background: 'none', color: '#1f4fd6', fontWeight: 800, cursor: 'pointer', fontFamily: 'inherit', fontSize: 'inherit', padding: 0, whiteSpace: 'nowrap' }}
                    >
                      보기 →
                    </button>
                  </b>
                </div>
              ))}
          </section>

          {/* (고도화 예정) 시안의 집행 요약 통계 카드 자리 — 월별 집행/환불/승인 누적 집계 API 마련 후 복원 */}
        </div>
      </div>

      {/* 진행 중인 프로젝트 */}
      <section className="mb-card" aria-label="진행 중인 프로젝트">
        <h3 style={{ fontSize: '15.5px', fontWeight: 800, letterSpacing: '-0.01em', marginBottom: 14 }}>진행 중인 프로젝트</h3>
        {loading && <div className="mbh-loading">불러오는 중…</div>}
        {!loading && cards.length === 0 && (
          <div className="mbh-todo__empty">진행 중인 프로젝트가 없어요. 첫 프로젝트를 등록해보세요.</div>
        )}
        {!loading && cards.length > 0 && (
          <div className="mbh-plist">
            {cards.map((c) => (
              <button key={c.id} type="button" className="mbh-pcard" onClick={c.onClick}>
                <span className="mbh-pcard__logo" aria-hidden="true">
                  {c.cover ? <img src={c.cover} alt="" /> : (c.name || '프').charAt(0)}
                </span>
                <span className="mbh-pcard__meta">
                  <span className="mbh-pcard__name">{c.name}</span>
                  <span className="mbh-pcard__sub">{c.sub}</span>
                </span>
                <span className="mbh-pcard__pipe">
                  <Pipeline stage={c.stage.n} />
                  <span className="mbh-pcard__stage">{c.stage.n}단계 · {c.stage.label}</span>
                </span>
                <span className="mbh-pcard__stat">
                  <span className={`mbh-pcard__num ${c.numClass}`}>{c.num}</span>
                  <span className="mbh-pcard__unit">{c.unit}</span>
                </span>
              </button>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

export default MarketerBizHome;
